/**
 * Per-step validation for the container deployment stepper.
 *
 * Every validator returns error KEYS, never rendered strings, so this module imports nothing from
 * React or i18next and runs under `node --test`. Resolve a key with
 * `t(`deployment.errors.${key}`, ERROR_DEFAULTS[key])`.
 */

import type { ContainerConfigFormValue } from '../components/ContainerConfigForm';
import type { GitHubSourceFormValue } from '../components/GitHubSourceForm';
import type { InlineSourceFormValue } from '../components/InlineSourceForm';
import type { SourceType } from '../components/SourceTypeSelector';
import type { UrlSourceFormValue } from '../components/UrlSourceForm';

const KEBAB_CASE = /^[a-z0-9-]+$/;

export const DEPLOYMENT_STEPS = ['source', 'configuration', 'review'] as const;
export type DeploymentStepId = (typeof DEPLOYMENT_STEPS)[number];

export const STEP_SOURCE = 0;
export const STEP_CONFIGURATION = 1;
export const STEP_REVIEW = 2;

export type DeploymentField =
    | 'name'
    | 'sourceUrlImage'
    | 'sourceInlineContent'
    | 'sourceGithub'
    | 'container_port'
    | 'cpu'
    | 'memory'
    | 'request_timeout'
    | 'request_concurrency'
    | 'min_replicas'
    | 'max_replicas';

/** Suffix under the `deployment.errors.` i18n namespace. */
export type ErrorKey =
    | 'nameRequired'
    | 'nameKebab'
    | 'nameLength'
    | 'imageRequired'
    | 'inlineRequired'
    | 'githubRequired'
    | 'githubOwnerOnly'
    | 'portRange'
    | 'cpuPositive'
    | 'memoryPositive'
    | 'timeoutPositive'
    | 'concurrencyPositive'
    | 'minReplicas'
    | 'maxReplicas'
    | 'replicaOrder';

/**
 * English fallbacks fed to t() as the default value, so a missing key still renders a sentence.
 * Keep in sync with deployment.errors.* in src/i18n/locales/{en,th}/containerEngine.json.
 */
export const ERROR_DEFAULTS: Record<ErrorKey, string> = {
    nameRequired: 'Name is required.',
    nameKebab: 'Lowercase letters, digits, and hyphens only.',
    nameLength: 'Name must be between 3 and 63 characters.',
    imageRequired: 'Container image is required.',
    inlineRequired: 'Dockerfile content is required.',
    githubRequired: 'Owner, repository, and branch are required.',
    githubOwnerOnly:
        'Only the project owner can deploy from GitHub. Choose a container image or an inline Dockerfile.',
    portRange: 'Port must be between 1 and 65535.',
    cpuPositive: 'CPU must be positive.',
    memoryPositive: 'Memory must be positive.',
    timeoutPositive: 'Request timeout must be positive.',
    concurrencyPositive: 'Concurrency must be positive.',
    minReplicas: 'Min replicas cannot be negative.',
    maxReplicas: 'Max replicas must be at least 1.',
    replicaOrder: 'Max replicas must be greater than or equal to min replicas.',
};

export type StepErrors = Partial<Record<DeploymentField, ErrorKey>>;

export interface SourceStepState {
    name: string;
    sourceType: SourceType;
    urlSource: UrlSourceFormValue;
    inlineSource: InlineSourceFormValue;
    githubSource: GitHubSourceFormValue;
    /** False in a project the caller doesn't own: GitHub sources are rejected. Defaults to true. */
    canUseOwnerSources?: boolean;
}

export interface DeploymentFormState extends SourceStepState {
    config: ContainerConfigFormValue;
}

export function validateSourceStep(state: SourceStepState): StepErrors {
    const errors: StepErrors = {};

    const name = state.name.trim();
    if (!name) errors.name = 'nameRequired';
    else if (!KEBAB_CASE.test(name)) errors.name = 'nameKebab';
    else if (name.length < 3 || name.length > 63) errors.name = 'nameLength';

    // Only the selected branch is checked. The other two keep whatever the user typed before
    // switching, and buildSource() never reads them — validating them would block a deploy on
    // data that is never sent.
    if (state.sourceType === 'url') {
        if (!state.urlSource.image.trim()) errors.sourceUrlImage = 'imageRequired';
    } else if (state.sourceType === 'inline') {
        if (!state.inlineSource.inline.trim()) errors.sourceInlineContent = 'inlineRequired';
    } else if (state.canUseOwnerSources === false) {
        errors.sourceGithub = 'githubOwnerOnly';
    } else {
        const github = state.githubSource;
        if (!github.github_owner || !github.github_repository || !github.github_branch) {
            errors.sourceGithub = 'githubRequired';
        }
    }

    return errors;
}

export function validateConfigurationStep(config: ContainerConfigFormValue): StepErrors {
    const errors: StepErrors = {};

    if (
        !Number.isFinite(config.container_port) ||
        config.container_port < 1 ||
        config.container_port > 65535
    ) {
        errors.container_port = 'portRange';
    }
    if (!Number.isFinite(config.cpu) || config.cpu <= 0) errors.cpu = 'cpuPositive';
    if (!Number.isFinite(config.memory) || config.memory <= 0) errors.memory = 'memoryPositive';
    if (!Number.isFinite(config.request_timeout) || config.request_timeout <= 0) {
        errors.request_timeout = 'timeoutPositive';
    }
    if (!Number.isFinite(config.request_concurrency) || config.request_concurrency <= 0) {
        errors.request_concurrency = 'concurrencyPositive';
    }
    if (config.min_replicas < 0) errors.min_replicas = 'minReplicas';

    // Ordered so a max below 1 reports the more actionable message rather than being overwritten
    // by the ordering rule.
    if (config.max_replicas < 1) errors.max_replicas = 'maxReplicas';
    else if (config.min_replicas > config.max_replicas) errors.max_replicas = 'replicaOrder';

    return errors;
}

/** Review owns no fields of its own — it is a read-only view of the two steps before it. */
export function validateStep(step: number, state: DeploymentFormState): StepErrors {
    if (step === STEP_SOURCE) return validateSourceStep(state);
    if (step === STEP_CONFIGURATION) return validateConfigurationStep(state.config);
    return {};
}

export function isStepValid(step: number, state: DeploymentFormState): boolean {
    return Object.keys(validateStep(step, state)).length === 0;
}
